import { useCallback, useState, useMemo } from 'react';
import {
  Popover,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import { gwtStyles } from '../../config/theme';
import { type ColumnConfig, DEFAULT_COLUMNS, saveColumnConfig } from './columnConfig';

interface ColumnConfigPopupProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
}

/**
 * Column configuration popup — right-click on grid header.
 * Lets users show/hide and reorder columns.
 * Mirrors GWT column configuration behavior.
 */
export const ColumnConfigPopup = ({
  anchorEl,
  onClose,
  columns,
  onColumnsChange,
}: ColumnConfigPopupProps) => {
  const [local, setLocal] = useState<ColumnConfig[]>(() => [...columns]);

  const visibleCount = useMemo(() => local.filter((c) => c.visible).length, [local]);

  const handleToggle = useCallback(
    (field: string) => {
      setLocal((prev) =>
        prev.map((c) =>
          c.field === field ? { ...c, visible: !c.visible } : c,
        ),
      );
    },
    [],
  );

  const handleMove = useCallback((field: string, direction: -1 | 1) => {
    setLocal((prev) => {
      const idx = prev.findIndex((c) => c.field === field);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    onColumnsChange(local);
    saveColumnConfig(local);
    onClose();
  }, [local, onColumnsChange, onClose]);

  const handleReset = useCallback(() => {
    const defaults = [...DEFAULT_COLUMNS];
    setLocal(defaults);
  }, []);

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 320,
          maxHeight: 480,
          border: `1px solid ${gwtStyles.borderSubtle}`,
          borderRadius: '6px',
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: gwtStyles.accentPrimary, mb: 0.5 }}>
          Column Configuration
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
          {visibleCount} of {local.length} columns visible
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ maxHeight: 340, overflow: 'auto', px: 0.5, py: 0.5 }}>
        {local.map((col, idx) => (
          <Box
            key={col.field}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.25,
              px: 0.5,
              borderRadius: 0.5,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <DragIcon sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5, cursor: 'grab' }} />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={col.visible}
                  onChange={() => handleToggle(col.field)}
                  sx={{ p: 0.25 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  {col.headerName}
                </Typography>
              }
              sx={{ flex: 1, mr: 0, ml: 0 }}
            />
            <IconButton
              size="small"
              disabled={idx === 0}
              onClick={() => handleMove(col.field, -1)}
              sx={{ width: 20, height: 20 }}
            >
              <UpIcon sx={{ fontSize: 12 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={idx === local.length - 1}
              onClick={() => handleMove(col.field, 1)}
              sx={{ width: 20, height: 20 }}
            >
              <DownIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1.5, py: 0.75 }}>
        <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: 11 }}>
          Reset Defaults
        </Button>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 11 }}>
            Cancel
          </Button>
          <Button
            size="small" variant="contained" onClick={handleApply}
            sx={{ textTransform: 'none', fontSize: 11 }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
